import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Paperclip } from 'lucide-react';
import '../../component-styles/Todos.css';
import '../../component-styles/KanbanBoard.css';
import useProjects from '../../hooks/useProjects';
import { triggerPlannerSync } from '../utils/syncEvent';
import API_BASE from '../../utils/api.js';
import KanbanBoard from './KanbanBoard';
import AssigneeAvatars from './AssigneeAvatars';
import TodoAttachments from './TodoAttachments';

function getApiBase() {
  return API_BASE;
}

function storageKeyTodos(userId) {
  return `todos-${userId || 'anon'}`;
}
function storageKeyView(userId) {
  return `todos-view-${userId || 'anon'}`;
}
function loadViewFromStorage(userId) {
  try {
    const raw = localStorage.getItem(storageKeyView(userId));
    return raw === 'board' || raw === 'list' ? raw : 'board';
  } catch {
    return 'board';
  }
}
function loadTodosFromStorage(userId) {
  try {
    const raw = localStorage.getItem(storageKeyTodos(userId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveTodosToStorage(userId, todos) {
  try {
    localStorage.setItem(storageKeyTodos(userId), JSON.stringify(todos));
  } catch {}
}
function isOverdue(dueDate, completed) {
  if (!dueDate || completed) return false;
  return dueDate < new Date().toISOString().slice(0, 10);
}

export default function TodoList({
  initialProjectId = '',
  projectName = '',
  hideProjectTitleProjectPill = false,
  hideProjectSelector = false,
}) {
  const userId = localStorage.getItem('userId') || null;
  const [projectScopeId] = useState(initialProjectId ? String(initialProjectId) : '');
  const [todos, setTodos] = useState(() => loadTodosFromStorage(userId) || []);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [team, setTeam] = useState([]);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [view, setView] = useState(() => loadViewFromStorage(userId));

  const [form, setForm] = useState({
    title: '',
    notes: '',
    dueDate: '',
    priority: 'normal',
    status: 'todo',
    project: '',
    assignees: [],
    attachments: [],
  });

  /* ---------------- Hub Filters (global view only) ---------------- */
  const [hubProjectFilter, setHubProjectFilter] = useState('all');
  const [hubAssigneeFilter, setHubAssigneeFilter] = useState('all');
  const [hubStatusFilter, setHubStatusFilter] = useState('all');
  const [hubDueStart, setHubDueStart] = useState('');
  const [hubDueEnd, setHubDueEnd] = useState('');

  const { projects: fetchedProjects } = useProjects({ activeOnly: true });
  const projects = fetchedProjects || [];

  const projectNameMap = useMemo(() => {
    const m = {};
    projects.forEach((p) => {
      m[String(p.id)] = p.name || p.projectNickname || '';
    });
    return m;
  }, [projects]);

  const modalRef = useRef(null);
  const assigneeRef = useRef(null);

  /* -------------------------------------------------
     CLOSE ASSIGNEE DROPDOWN ON OUTSIDE CLICK
  -------------------------------------------------- */
  useEffect(() => {
    function handleOutside(e) {
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) {
        setAssigneeOpen(false);
        setAssigneeSearch('');
      }
    }

    if (assigneeOpen) {
      document.addEventListener('mousedown', handleOutside);
    }

    return () => document.removeEventListener('mousedown', handleOutside);
  }, [assigneeOpen]);

  /* ---------------- Fetch Todos ---------------- */
  async function refreshTodos() {
    if (!userId) return;
    try {
      const res = await fetch(`${getApiBase()}/api/todos/${encodeURIComponent(userId)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Bad response');
      const data = await res.json();

      const normalized = (data || []).map((t) => ({
        todoId: t.todoId || t.id || '',
        title: t.title || '',
        notes: t.notes || '',
        completed: Boolean(t.completed),
        status: t.status || (t.completed ? 'done' : 'todo'),
        dueDate: t.dueDate || '',
        priority: t.priority || 'normal',
        projectId: t.projectId ? String(t.projectId) : '',
        assignees: Array.isArray(t.assignees)
          ? t.assignees.map((a) => (typeof a === 'string' ? a : a.email)).filter(Boolean)
          : [],
        attachments: Array.isArray(t.attachments) ? t.attachments : [],
      }));

      setTodos(normalized);
      saveTodosToStorage(userId, normalized);
    } catch (err) {
      console.error('Failed to refresh todos', err);
    }
  }

  useEffect(() => {
    // `silent` is true for background refreshes (e.g. right after adding/editing
    // a task) so they update the list in place instead of flashing a loading state.
    async function fetchTodos(silent = false) {
      if (!silent) setLoading(true);
      if (!userId) {
        setTodos(loadTodosFromStorage(userId) || []);
        if (!silent) setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${getApiBase()}/api/todos/${encodeURIComponent(userId)}`, {
          credentials: 'include',
        });
        const arr = await res.json();

        const normalized = (arr || []).map((t) => ({
          todoId: t.todoId || t.id || '',
          title: t.title || '',
          notes: t.notes || '',
          completed: Boolean(t.completed),
          status: t.status || (t.completed ? 'done' : 'todo'),
          dueDate: t.dueDate || '',
          priority: t.priority || 'normal',
          projectId: t.projectId !== undefined && t.projectId !== null ? String(t.projectId) : '',
          assignees: Array.isArray(t.assignees)
            ? t.assignees.map((a) => (typeof a === 'string' ? a : a.email)).filter(Boolean)
            : [],
          attachments: Array.isArray(t.attachments) ? t.attachments : [],
        }));

        const final = projectScopeId
          ? normalized.filter((x) => String(x.projectId) === String(projectScopeId))
          : normalized;

        setTodos(final);
        saveTodosToStorage(userId, normalized);
      } catch {
        if (!silent) setTodos(loadTodosFromStorage(userId) || []);
      } finally {
        if (!silent) setLoading(false);
      }
    }

    fetchTodos(false);
    const onSync = () => fetchTodos(true);
    window.addEventListener('plannerSyncUpdated', onSync);
    return () => window.removeEventListener('plannerSyncUpdated', onSync);
  }, [userId, projectScopeId]);

  useEffect(() => {
    if (!userId) return;
    fetch(`${getApiBase()}/api/settings/${encodeURIComponent(userId)}`, {
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => setTeam(Array.isArray(data.team) ? data.team : []))
      .catch(() => setTeam([]));
  }, [userId]);

  /* ---------------- Modal Controls ---------------- */
  function openAddModal(editTodo = null) {
    setEditing(editTodo);

    if (editTodo) {
      setForm({
        title: editTodo.title || '',
        notes: editTodo.notes || '',
        dueDate: editTodo.dueDate || '',
        priority: editTodo.priority || 'normal',
        status: editTodo.status || (editTodo.completed ? 'done' : 'todo'),
        project: editTodo.projectId || projectScopeId || '',
        assignees: Array.isArray(editTodo.assignees) ? editTodo.assignees : [],
        attachments: Array.isArray(editTodo.attachments) ? editTodo.attachments : [],
      });
    } else {
      setForm({
        title: '',
        notes: '',
        dueDate: '',
        priority: 'normal',
        status: 'todo',
        project: projectScopeId || '',
        assignees: [],
        attachments: [],
      });
    }

    setAssigneeSearch('');
    setAssigneeOpen(false);
    setShowAdd(true);
  }

  function closeAddModal() {
    setShowAdd(false);
    setEditing(null);
    setAssigneeOpen(false);
    setAssigneeSearch('');
  }

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') closeAddModal();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ---------------- Create or Update ---------------- */
  async function createOrUpdate(e) {
    e.preventDefault();
    if (!form.title.trim()) return;

    if (editing) {
      const updated = {
        ...editing,
        ...form,
        projectId: form.project || '',
        completed: form.status === 'done',
      };
      setTodos((prev) => prev.map((t) => (t.todoId === editing.todoId ? updated : t)));
      closeAddModal();
      if (!userId) return;

      try {
        const res = await fetch(
          `${getApiBase()}/api/todos/${encodeURIComponent(userId)}/${encodeURIComponent(
            editing.todoId
          )}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              ...updated,
              assignees: form.assignees,
              projectName: projectNameMap[form.project || projectScopeId] || projectName || '',
            }),
          }
        );
        if (res.ok) {
          triggerPlannerSync();
          await refreshTodos();
        }
      } catch (err) {
        console.error('Error updating todo:', err);
      }
    } else {
      const id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const newTodo = {
        todoId: id,
        title: form.title,
        notes: form.notes,
        dueDate: form.dueDate,
        priority: form.priority || 'normal',
        status: form.status || 'todo',
        completed: form.status === 'done',
        createdAt: new Date().toISOString(),
        projectId: form.project || projectScopeId || '',
        attachments: form.attachments || [],
      };
      setTodos((p) => [newTodo, ...p]);
      closeAddModal();

      if (!userId) {
        saveTodosToStorage(userId, [newTodo, ...todos]);
        return;
      }
      try {
        const res = await fetch(`${getApiBase()}/api/todos/sync`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userId,
            title: newTodo.title,
            notes: newTodo.notes,
            dueDate: newTodo.dueDate,
            priority: newTodo.priority,
            status: newTodo.status,
            completed: newTodo.completed,
            projectId: form.project || projectScopeId || '',
            projectName: projectNameMap[form.project || projectScopeId] || projectName || '',
            assignees: form.assignees,
            attachments: form.attachments || [],
          }),
        });
        if (res.ok) {
          triggerPlannerSync();
          await refreshTodos();
        }
      } catch (err) {
        console.error('Error creating todo:', err);
      }
    }
  }

  /* ---------------- Remove ---------------- */
  async function removeTodo(todoId) {
    setTodos((prev) => prev.filter((t) => t.todoId !== todoId));
    if (!userId) return;
    try {
      const res = await fetch(
        `${getApiBase()}/api/todos/${encodeURIComponent(userId)}/${encodeURIComponent(todoId)}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (res.ok) {
        triggerPlannerSync();
        await refreshTodos();
      }
    } catch (err) {
      console.error('Error deleting todo:', err);
    }
  }

  /* ---------------- Toggle Complete ---------------- */
  async function toggleComplete(todoId) {
    const todo = todos.find((t) => t.todoId === todoId);
    if (!todo) return;

    const newCompleted = !todo.completed;
    const newStatus = newCompleted ? 'done' : 'todo';

    // 🟢 Optimistic update first
    const updatedList = todos.map((t) =>
      t.todoId === todoId ? { ...t, completed: newCompleted, status: newStatus } : t
    );
    setTodos(updatedList);
    if (userId) saveTodosToStorage(userId, updatedList);

    // skip unsynced local todos
    if (!userId || todo.todoId.startsWith('local-')) return;

    try {
      const res = await fetch(
        `${getApiBase()}/api/todos/${encodeURIComponent(userId)}/${encodeURIComponent(todoId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...todo,
            completed: newCompleted,
            status: newStatus,
            projectName: todo.projectName || '',
            projectId: todo.projectId || '',
            dueDate: todo.dueDate || '',
          }),
        }
      );

      if (res.ok) {
        // 🔄 notify calendar to re-fetch its events
        triggerPlannerSync();

        // 🕒 wait a bit for DynamoDB + syncService to finish
        await new Promise((r) => setTimeout(r, 600));

        // 🔁 re-fetch updated todos
        await refreshTodos();
      } else {
        console.warn('Server rejected PUT, reverting change');
        setTodos((prev) =>
          prev.map((t) =>
            t.todoId === todoId ? { ...t, completed: !newCompleted, status: todo.status } : t
          )
        );
      }
    } catch (err) {
      console.error('Failed to update todo completion:', err);
      setTodos((prev) =>
        prev.map((t) => (t.todoId === todoId ? { ...t, completed: !newCompleted } : t))
      );
    }
  }

  /* ---------------- Change Status (Kanban drag) ---------------- */
  async function changeStatus(todoId, newStatus) {
    const todo = todos.find((t) => t.todoId === todoId);
    if (!todo) return;

    const prevStatus = todo.status || 'todo';
    const prevCompleted = todo.completed;
    const newCompleted = newStatus === 'done';

    const updatedList = todos.map((t) =>
      t.todoId === todoId ? { ...t, status: newStatus, completed: newCompleted } : t
    );
    setTodos(updatedList);
    if (userId) saveTodosToStorage(userId, updatedList);

    if (!userId || todo.todoId.startsWith('local-')) return;

    try {
      const res = await fetch(
        `${getApiBase()}/api/todos/${encodeURIComponent(userId)}/${encodeURIComponent(todoId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            ...todo,
            status: newStatus,
            completed: newCompleted,
            projectName: todo.projectName || '',
            projectId: todo.projectId || '',
            dueDate: todo.dueDate || '',
          }),
        }
      );

      if (res.ok) {
        triggerPlannerSync();
        await new Promise((r) => setTimeout(r, 600));
        await refreshTodos();
      } else {
        console.warn('Server rejected status update, reverting change');
        setTodos((prev) =>
          prev.map((t) =>
            t.todoId === todoId ? { ...t, status: prevStatus, completed: prevCompleted } : t
          )
        );
      }
    } catch (err) {
      console.error('Failed to update todo status:', err);
      setTodos((prev) =>
        prev.map((t) =>
          t.todoId === todoId ? { ...t, status: prevStatus, completed: prevCompleted } : t
        )
      );
    }
  }

  function changeView(next) {
    setView(next);
    try {
      localStorage.setItem(storageKeyView(userId), next);
    } catch {}
  }

  /* ---------------- Computed Lists ---------------- */
  const filteredTodos = useMemo(() => {
    if (projectScopeId) {
      return todos.filter((t) => String(t.projectId || '') === String(projectScopeId));
    }

    return todos.filter((t) => {
      if (hubProjectFilter === '__none__' && t.projectId) return false;
      if (
        hubProjectFilter !== 'all' &&
        hubProjectFilter !== '__none__' &&
        String(t.projectId || '') !== hubProjectFilter
      )
        return false;
      if (hubAssigneeFilter !== 'all' && !(t.assignees || []).includes(hubAssigneeFilter))
        return false;
      if (hubDueStart && t.dueDate && t.dueDate < hubDueStart) return false;
      if (hubDueEnd && t.dueDate && t.dueDate > hubDueEnd) return false;
      return true;
    });
  }, [todos, projectScopeId, hubProjectFilter, hubAssigneeFilter, hubDueStart, hubDueEnd]);

  const showActiveSection = hubStatusFilter !== 'completed';
  const showCompletedSection = hubStatusFilter !== 'active';

  const counts = useMemo(() => {
    const total = filteredTodos.length;
    const remaining = filteredTodos.filter((t) => !t.completed).length;
    return { total, remaining };
  }, [filteredTodos]);

  const filteredTeam = useMemo(() => {
    if (!assigneeSearch.trim()) return team;

    const q = assigneeSearch.toLowerCase();
    return team.filter(
      (m) => m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    );
  }, [team, assigneeSearch]);

  const activeTodos = filteredTodos.filter((t) => !t.completed);
  const completedTodos = filteredTodos.filter((t) => t.completed);

  /* ---------------- Render ---------------- */
  return (
    <div className="todos-page">
      <div className="todos-heading">
        <div className="todos-left">
          <div className="todos-left-inner">
            <h2 className="todos-title">{projectScopeId ? 'To Do List' : 'To-Do Hub'}</h2>
            {projectName && !hideProjectTitleProjectPill ? (
              <div className="project-subtitle">{projectName}</div>
            ) : null}
          </div>
        </div>
        <div className="todos-controls">
          <div className="todos-counts">
            <span className="muted">{counts.remaining}</span> remaining / {counts.total}
          </div>
          <div className="view-toggle">
            <button
              type="button"
              className={view === 'list' ? 'active' : ''}
              onClick={() => changeView('list')}
            >
              List
            </button>
            <button
              type="button"
              className={view === 'board' ? 'active' : ''}
              onClick={() => changeView('board')}
            >
              Board
            </button>
          </div>
          <button className="btn-add" onClick={() => openAddModal(null)}>
            <span className="plus">+</span>
            <span className="btn-add-text">Add New</span>
          </button>
        </div>
      </div>

      {!projectScopeId && (
        <div className="todos-hub-filters">
          <select value={hubProjectFilter} onChange={(e) => setHubProjectFilter(e.target.value)}>
            <option value="all">All Projects</option>
            <option value="__none__">Internal / Non-Project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.projectNickname || p.id}
              </option>
            ))}
          </select>

          <select value={hubAssigneeFilter} onChange={(e) => setHubAssigneeFilter(e.target.value)}>
            <option value="all">All Team Members</option>
            {team.map((m) => (
              <option key={m.email} value={m.email}>
                {m.name || m.email}
              </option>
            ))}
          </select>

          <select value={hubStatusFilter} onChange={(e) => setHubStatusFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="active">Open</option>
            <option value="completed">Completed</option>
          </select>

          <input
            type="date"
            value={hubDueStart}
            title="Due after"
            onChange={(e) => setHubDueStart(e.target.value)}
          />
          <input
            type="date"
            value={hubDueEnd}
            title="Due before"
            onChange={(e) => setHubDueEnd(e.target.value)}
          />
        </div>
      )}

      {view === 'board' ? (
        <div className="todos-card">
          {loading ? (
            <div className="empty muted">Loading todos…</div>
          ) : filteredTodos.length === 0 ? (
            <div className="empty muted">No tasks. Click Add New to create one.</div>
          ) : (
            <KanbanBoard
              todos={filteredTodos}
              team={team}
              projectNameMap={projectNameMap}
              projectScoped={!!projectScopeId}
              onStatusChange={changeStatus}
              onEdit={openAddModal}
            />
          )}
        </div>
      ) : (
      <div className="todos-card">
        {loading ? (
          <div className="empty muted">Loading todos…</div>
        ) : filteredTodos.length === 0 ? (
          <div className="empty muted">No tasks. Click Add New to create one.</div>
        ) : (
          <>
            {showActiveSection && (
            <ul className="todos-list">
              {activeTodos.map((t) => (
                <li
                  key={t.todoId}
                  className={`todo-row priority-${t.priority || 'normal'}`}
                >
                  <label className="todo-left">
                    <input
                      type="checkbox"
                      checked={!!t.completed}
                      onChange={() => toggleComplete(t.todoId)}
                    />
                    <div className="todo-main">
                      <div className="todo-title">{t.title}</div>

                      <div className="todo-meta">
                        {t.dueDate && (
                          <span
                            className={`todo-tag todo-tag-date ${
                              isOverdue(t.dueDate, t.completed) ? 'overdue' : ''
                            }`}
                          >
                            Due {t.dueDate}
                          </span>
                        )}
                        <span className={`priority-badge priority-${t.priority || 'normal'}`}>
                          {t.priority || 'normal'}
                        </span>
                        {t.attachments?.length > 0 && (
                          <span
                            className="todo-tag todo-tag-attachments"
                            title={t.attachments.map((a) => a.name).join(', ')}
                          >
                            <Paperclip size={11} />
                            {`${t.attachments.length} ${
                              t.attachments.length === 1 ? 'file' : 'files'
                            }`}
                          </span>
                        )}
                        {t.projectId ? (
                          <span className="todo-tag todo-tag-project">
                            {projectNameMap[String(t.projectId)] || 'Project'}
                          </span>
                        ) : !projectScopeId ? (
                          <span className="todo-tag">Internal / Non-Project</span>
                        ) : null}
                      </div>
                    </div>
                  </label>

                  <AssigneeAvatars assignees={t.assignees} team={team} />

                  <div className="todo-actions">
                    <button className="link-btn" onClick={() => openAddModal(t)}>
                      Edit
                    </button>
                    <button className="link-btn danger" onClick={() => removeTodo(t.todoId)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            )}

            {showCompletedSection && completedTodos.length > 0 && (
              <div className="completed-section">
                <h4 className="completed-heading">Completed ({completedTodos.length})</h4>
                <ul className="todos-list completed">
                  {completedTodos.map((t) => (
                    <li key={t.todoId} className="todo-row done">
                      <label className="todo-left">
                        <input
                          type="checkbox"
                          checked={!!t.completed}
                          onChange={() => toggleComplete(t.todoId)}
                        />
                        <div className="todo-main">
                          <div className="todo-title">{t.title}</div>
                        </div>
                      </label>

                      <AssigneeAvatars assignees={t.assignees} team={team} />

                      <div className="todo-actions">
                        <button className="link-btn danger" onClick={() => removeTodo(t.todoId)}>
                          Delete
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {showAdd && (
        <div
          className="todo-modal-overlay show"
          onMouseDown={(e) => {
            if (modalRef.current && modalRef.current.contains(e.target)) return;
            closeAddModal();
          }}
        >
          <div className="todo-modal-card show" ref={modalRef}>
            {/* HEADER */}
            <div className="todo-modal-head">
              <h3 className="todo-modal-title">{editing ? 'Edit Task' : 'Add Task'}</h3>
              <button className="todo-modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>

            {/* FORM */}
            <form className="todo-modal-form" onSubmit={createOrUpdate}>
              <label>
                <span>Title</span>
                <input
                  value={form.title}
                  onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                  required
                />
              </label>

              <label>
                <span>Due Date</span>
                <input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm((s) => ({ ...s, dueDate: e.target.value }))}
                />
              </label>

              <label>
                <span>Priority</span>
                <select
                  value={form.priority}
                  onChange={(e) => setForm((s) => ({ ...s, priority: e.target.value }))}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </label>

              <label>
                <span>Status</span>
                <select
                  value={form.status}
                  onChange={(e) => setForm((s) => ({ ...s, status: e.target.value }))}
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="done">Done</option>
                </select>
              </label>

              <div className={`assignees-field ${assigneeOpen ? 'active' : ''}`} ref={assigneeRef}>
                <div className="assignees-header">Assign to</div>

                <div className="assignee-input" onClick={() => setAssigneeOpen(true)}>
                  {/* Selected assignees */}
                  {form.assignees.map((email) => {
                    const member = team.find((m) => m.email === email);
                    return (
                      <span key={email} className="assignee-pill">
                        {member?.name || email}
                        <button
                          type="button"
                          className="assignee-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setForm((s) => ({
                              ...s,
                              assignees: s.assignees.filter((x) => x !== email),
                            }));
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}

                  {/* Search input */}
                  <input
                    className="assignee-search"
                    type="text"
                    placeholder={form.assignees.length === 0 ? 'Search people…' : ''}
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    onFocus={() => setAssigneeOpen(true)}
                  />
                </div>

                {assigneeOpen && (
                  <div className="assignee-list scrollable">
                    {filteredTeam.length === 0 ? (
                      <div className="assignee-empty">No matches</div>
                    ) : (
                      filteredTeam.map((m) => {
                        const key = m.email;
                        const checked = form.assignees.includes(key);

                        return (
                          <label
                            key={key}
                            className={`assignee-option ${checked ? 'checked' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() =>
                                setForm((s) => ({
                                  ...s,
                                  assignees: checked
                                    ? s.assignees.filter((x) => x !== key)
                                    : [...s.assignees, key],
                                }))
                              }
                            />
                            {m.name}
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {!hideProjectSelector && (
                <label>
                  <span>Project</span>
                  <select
                    value={form.project || projectScopeId || ''}
                    onChange={(e) => setForm((s) => ({ ...s, project: e.target.value }))}
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.projectNickname || p.title || p.id}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="attachments-full">
                <TodoAttachments form={form} setForm={setForm} />
              </div>

              <label className="notes-full">
                <span>Notes</span>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                  rows={5}
                />
              </label>

              {/* ACTIONS */}
              <div className="todo-modal-actions">
                <button type="button" className="todo-btn cancel" onClick={closeAddModal}>
                  Cancel
                </button>

                <button type="submit" className="todo-btn save">
                  {editing ? 'Save' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
